import React, { useEffect } from 'react'

import { Modal, Select, Divider, Text } from '@mantine/core'
import { useForm } from '@mantine/form'
import { File } from 'lucide-react'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DateInput from 'src/components/Inputs/DateInput/DateInput'
import DateShortcutButton from 'src/components/Inputs/DateShortcutButton/DateShortcutButton'
import {
  DocumentCategory,
  DocumentUserStatus,
} from 'src/graphql/types/documents'
import { buildDocumentCategoryOptions } from 'src/lib/documentCategory.utils'
import {
  formatSnakeValue,
  formatBytes,
  formatDocumentCategory,
} from 'src/lib/formatters'

const categories = buildDocumentCategoryOptions(formatDocumentCategory)

const statuses = [
  DocumentUserStatus.PENDING_REVIEW,
  DocumentUserStatus.COMPLETED,
]

export interface BulkUploadDocumentReviewModalProps {
  isOpen: boolean
  onClose: () => void
  files: File[]
  onSubmit: (data: {
    category: DocumentCategory
    userStatus: DocumentUserStatus
    expirationDate: Date | null
  }) => void
  /**
   * Files dropped from a VoE. They are all supporting files: the status is
   * fixed at Pending Review (never Completed) and the employer's response has
   * to be uploaded on its own, where it records how the answer arrived.
   */
  isVoEUpload?: boolean
}

const BulkUploadDocumentReviewModal = ({
  isOpen,
  onClose,
  files,
  onSubmit,
  isVoEUpload = false,
}: BulkUploadDocumentReviewModalProps) => {
  const form = useForm({
    initialValues: {
      category: '',
      userStatus: DocumentUserStatus.PENDING_REVIEW,
      expirationDate: null,
    },
    validate: {
      category: (value) => (value ? null : 'Category is required'),
      userStatus: (value) => (value ? null : 'Status is required'),
    },
  })

  // Same default as a single VoE upload. The modal stays mounted, so set it on
  // each open rather than in initialValues.
  useEffect(() => {
    if (isOpen && isVoEUpload && !form.values.category) {
      form.setFieldValue(
        'category',
        DocumentCategory.VERIFICATION_OF_EMPLOYMENT
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isVoEUpload])

  const handleSubmit = form.onSubmit((values) => {
    onSubmit({
      category: values.category as DocumentCategory,
      userStatus: isVoEUpload
        ? DocumentUserStatus.PENDING_REVIEW
        : (values.userStatus as DocumentUserStatus),
      expirationDate: values.expirationDate,
    })
  })

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      classNames={{ title: 'font-semibold' }}
      title="Bulk Upload Documents"
      centered
      size="md"
      stackId="bulk-upload-review-modal"
    >
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4">
          {/* Files summary */}
          <div>
            <Text size="sm" fw={500} className="mb-2">
              Files to upload ({files.length})
            </Text>
            <div className="max-h-48 space-y-1.5 overflow-y-auto rounded-lg border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 p-3">
              {files.map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="flex items-center gap-2 truncate">
                    <File
                      size={14}
                      className="flex-shrink-0 stroke-doubleNickel-gray-500"
                    />
                    <span className="truncate text-doubleNickel-gray-700">
                      {file.name}
                    </span>
                  </span>
                  <span className="ml-2 flex-shrink-0 text-xs text-doubleNickel-gray-500">
                    {formatBytes(file.size)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Metadata form */}
          <div className="space-y-3">
            <Select
              withAsterisk
              label="Category"
              placeholder="Select category"
              data={categories}
              searchable
              required
              {...form.getInputProps('category')}
            />
            {isVoEUpload ? (
              <div className="rounded-lg bg-doubleNickel-gray-50 px-3 py-2.5 text-xs text-doubleNickel-gray-600">
                To complete the verification, upload the employer&apos;s
                response on its own.
              </div>
            ) : (
              <Select
                withAsterisk
                label="Status"
                placeholder="Select status"
                data={statuses.map((status) => ({
                  label: formatSnakeValue(status),
                  value: status,
                }))}
                required
                {...form.getInputProps('userStatus')}
              />
            )}
            <div className="flex flex-col">
              <DateInput
                label="Expiration Date"
                clearable
                value={form.values.expirationDate}
                onChange={(date) => form.setFieldValue('expirationDate', date)}
              />
              <DateShortcutButton
                onClick={(_event, date) =>
                  form.setFieldValue('expirationDate', date)
                }
              />
            </div>
          </div>

          <Divider variant="dashed" className="my-2" />

          {/* Buttons */}
          <div className="flex gap-3">
            <DNButton className="flex-1" variant="outline" onClick={onClose}>
              Cancel
            </DNButton>
            <DNButton
              className="flex-1"
              variant="primary"
              type="submit"
              disabled={!form.isValid()}
            >
              Upload {files.length} files
            </DNButton>
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default BulkUploadDocumentReviewModal
